import { useCallback, useState } from "react"
import usePortal from "react-useportal"
import * as bootstrap from "bootstrap";
import { useNavigate } from "react-router";
import { ConfirmDispatchAction } from "../../components/todos/confirmDispatchAction";
import { ConfirmNavigateAction } from "../../components/todos/confirmNavigateAction";
import { TodoAction, parseTodoAction } from "../../../lib/todos/types";
import { useSentry } from "../../providers/sentry";
import { useAppDispatch } from "../reduxToolkit";
import { completeTodo, RealizedTodoModel } from "../../reduxToolkit/todosSlice";

/**
 * Creates an onClick handler for a ToDo list item that will perform the action associated with the todo item.
 *
 * The action is performed in a modal dialog that is created and destroyed as needed.  The modal must be mounted
 * by the caller.
 * @returns [onClick, { currentAction }, modal]
 * @example
 *  const [onClick, { currentAction }, modal] = useTodoCompletion()
 *
 *  return <div className="todo-list">
 *    {incompleteTodos.map((todo) => <ToDoListItem key={todo.key} onClick={() => onClick(todo)} />)}
 *    {modal}
 *  </div>
 */
export function useTodoCompletion() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { captureException } = useSentry()

  const [currentTodo, setCurrentTodo] = useState<RealizedTodoModel | null>(null)

  const { Portal } = usePortal()
  const [uniqueId, setUniqueId] = useState<number>(Math.random())
  const domId = `modal-todo-list-${uniqueId}`

  const onClick = useCallback((todo: RealizedTodoModel) => {
    const currentAction: TodoAction | null = parseTodoAction(todo.action)

    if (todo.action && !currentAction) {
      // Degrading silently would leave a checklist row that does nothing when tapped, which
      // looks like a dead button rather than a bug. `title` and the action body can name a
      // provider or a condition, so report the shape and the ids, not the contents.
      captureException(new Error('Unusable ToDo action'), {
        tags: { feature: 'todos', todo_op: 'complete' },
        extra: {
          todo_id: todo.id,
          todo_type: todo.todo_type,
          record_type: todo.record_type,
          action_typeof: typeof todo.action,
          action_type: (todo.action as { type?: unknown } | null)?.type,
        },
      })
    }

    if (currentAction?.type === 'dispatch') {
      if (!currentAction.confirm) {
        // Immediately dispatch w/o confirmation
        dispatch(currentAction.action)
        // and mark the todo as complete
        onCompleted()
        return
      }
    }
    if (currentAction?.type === 'navigate') {
      if (!currentAction.confirm) {
        // Immediately dispatch w/o confirmation
        navigate(currentAction.route, { state: currentAction.state })
        // and mark the todo as complete
        onCompleted()
        return
      }
    }

    const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById(domId)!)
    modal?.show()
    setCurrentTodo(todo)
  }, [captureException, dispatch, domId, navigate])

  const onCompleted = useCallback(() => {
    const modal = bootstrap.Modal.getInstance(document.getElementById(domId)!)
    modal?.hide()
    setUniqueId(Math.random())
    
    if (currentTodo) {
      // completeTodo is mapped in syncSlice, so this is pushed to the server.
      // The backend owns creating and deleting todos, but the member marking one
      // done is a real write we have to send -- it used to reach the server only
      // by accident, through syncDown's divergence-repair pass.
      const now = new Date().toISOString()
      dispatch(completeTodo({
        id: currentTodo.id,
        updated_at: now,
        completed_at: now
      }))
    }

    setCurrentTodo(null)
  }, [domId, currentTodo, dispatch])

  const onCancelled = useCallback(() => {
    const modal = bootstrap.Modal.getInstance(document.getElementById(domId)!)
    modal?.hide()
    setUniqueId(Math.random())
    setCurrentTodo(null)
  }, [domId])

  // An action we cannot run must not decide whether the modal renders. This is render, so
  // throwing here unmounts the whole React tree -- what an unrecognised `navigate` action
  // did on staging -- and the member loses the entire page over one bad checklist row.
  // Fall back to the plain "did you complete this?" confirmation instead: the todo is still
  // completable by hand, and the failure is reported rather than displayed.
  const currentAction: TodoAction | null = parseTodoAction(currentTodo?.action)

  let title: string | null = null
  let action: JSX.Element | null = null
  if (currentAction) {
    switch(currentAction.type) {
      case 'dispatch':
        action = <ConfirmDispatchAction confirm={currentAction.confirm} action={currentAction.action} onCompleted={onCompleted} onCancelled={onCancelled} />
        break;
      case 'navigate':
        action = <ConfirmNavigateAction route={currentAction.route} confirm={currentAction.confirm} onCompleted={onCompleted} onCancelled={onCancelled} />
        break;
      default: {
        // Compile-time exhaustiveness only. parseTodoAction returns null for anything it
        // does not recognise, so this branch is unreachable at runtime -- and unlike the
        // throw it replaces, it cannot take the app down if that ever stops being true.
        const unreachable: never = currentAction
        void unreachable
        action = <ConfirmCompleteTodo todo={currentTodo!} onCompleted={onCompleted} onCancelled={onCancelled} />
      }
    }
  } else if (currentTodo) {
    action = <ConfirmCompleteTodo todo={currentTodo} onCompleted={onCompleted} onCancelled={onCancelled} />
  }


  const modal = <Portal>
      <div className="modal modal-lg fade" id={domId} aria-hidden="true">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title fs-5" id="exampleModalLabel">{title}</h2>
              <button type="button" className="btn-close"
                onClick={onCancelled}
                data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              {action}
            </div>
          </div>
        </div>
      </div>
    </Portal>

  return [
    onClick,
    {
      currentAction: currentAction ?? undefined,
    },
    modal
  ] as const
}

interface ConfirmCompleteTodoProps {
  todo: RealizedTodoModel
  onCompleted: () => void
  onCancelled: () => void
}

function ConfirmCompleteTodo({ todo, onCompleted, onCancelled }: ConfirmCompleteTodoProps) {
  return <div className="row">
    <div className="col">
      <h3 style={{textAlign: 'center'}}>Did you complete this?</h3>
      <p>
        This will mark the following checklist item as complete and remove it from the list:
      </p>
      <h5><i>{todo.title}</i></h5>
      <div className="w-100 d-flex justify-content-center">
        <div className="btn-group btn-group-lg">
          <button type="button" className="btn btn-primary"
            onClick={onCompleted}>
              Yes
          </button>
          <button type="button" className="btn btn-outline-secondary"
            onClick={onCancelled}>
              No
          </button>
        </div>
      </div>
    </div>
  </div>
}
